/**
 * `src/seo/` 纯函数层的共享类型定义。
 *
 * 这一层的全部导出都是纯数据与纯函数：不读 DOM、不发网络请求、
 * 不读除 `import.meta.env` 外的全局状态，对相同输入永远返回相同输出。
 * 运行时（`src/utils/seo.ts`）、构建期预渲染（`scripts/prerender/*`）与
 * 巡检器（`scripts/geo-inspect/*`）共用同一份源码，保证三者口径一致。
 *
 * 对应需求：8.14、9.2、10.6、10.13、10.14、12.2
 */

/**
 * Page_Type 全集（需求 Glossary 定义的 12 种页面类型）。
 * 数组顺序即 Glossary 的定义顺序，`PageType` 由本数组派生，
 * 新增页面类型只需在此处追加一项。
 */
export const PAGE_TYPES = [
  "HOME",
  "SOLUTION_LIST",
  "SOLUTION_CATEGORY",
  "SOLUTION_DETAIL",
  "PRODUCT_LIST",
  "PRODUCT_DETAIL",
  "CASE_LIST",
  "CASE_DETAIL",
  "NEWS_LIST",
  "NEWS_DETAIL",
  "ABOUT",
  "CONTACT",
] as const;

/** 页面类型联合类型，由 `PAGE_TYPES` 派生，二者不可能不同步 */
export type PageType = (typeof PAGE_TYPES)[number];

/** sitemaps.org 0.9 协议允许的 `<changefreq>` 取值 */
export type ChangeFreq =
  | "always"
  | "hourly"
  | "daily"
  | "weekly"
  | "monthly"
  | "yearly"
  | "never";

/** Open Graph `og:type` 在本站使用到的取值（需求 2.8、2.9） */
export type OgType = "website" | "article" | "product";

/**
 * 单个页面的内容质量下限（需求 10）。
 * `/contact` 的放宽值（h2Min=2、imgMin=1、visibleTextMin=400，需求 10.13、10.14、10.6）
 * 以数据形式落在 `PAGE_TYPE_META_PRESET` 里，而不是散落成
 * `if (pageType === 'CONTACT')` 的分支：放宽一旦回收，改一行数据即可。
 */
export type ContentBounds = {
  /** `<h2>` 元素数量下限 */
  h2Min: number;
  /** `<img>` 元素数量下限 */
  imgMin: number;
  /** Visible_Text_Length 下限（中文按 1 字符计） */
  visibleTextMin: number;
  /** 指向其他 Indexable_URL 的内部链接数量下限（需求 10.9，无 Page_Type 差异） */
  internalLinkMin: number;
};

/** 单个 Page_Type 的兜底文案与质量下限 */
export type PageTypePreset = {
  /** 标题模板，含 `{name}` 占位符 */
  titleTemplate: string;
  /** 内容标题缺失时使用的兜底标题，长度已保证 20-60 字符（需求 2.18） */
  fallbackTitle: string;
  /** 描述模板，含 `{name}` 占位符，用于把描述补足到 70 字符以上（需求 2.3、2.15） */
  descriptionTemplate: string;
  /** `buildKeywords()` 的补齐来源，每项 2-20 字符（需求 2.16） */
  keywordSeeds: string[];
  /** 主图缺失时使用的兜底社交分享图，绝对地址（需求 2.4、4.16） */
  fallbackOgImage: string;
  /** BreadcrumbList 中该层级的 `name`，2-40 字符（需求 4.1） */
  breadcrumbLabel: string;
  /** `og:type` 取值（需求 2.8、2.9） */
  ogType: OgType;
  /** 内容质量下限（需求 10） */
  bounds: ContentBounds;
  /** 是否在该 Page_Type 渲染 FAQ_Block 与 `FAQPage` JSON-LD（需求 6.1、6.12） */
  renderFaq: boolean;
};

/** sitemap 中单个 `<url>` 条目的更新频率与权重（需求 8.14） */
export type SitemapFrequency = {
  changefreq: ChangeFreq;
  /**
   * 存字符串而非数字：`1.0` 若以数字存储会被序列化成 `1`，
   * 与需求 8.14 逐字符给定的取值不一致。
   */
  priority: string;
};
